import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { api, errorMessage } from '../../shared/api/client';
import { Alert, Button, Card, EmptyState, ErrorState, Field, Loading } from '../../shared/ui/components';
import { useAsync } from '../../shared/ui/useAsync';
import { useMe } from '../session/SessionProvider';

type ChainItem = { id: string; title: string; startsAt: string; endsAt: string; slotMinutes: number; totalSlots: number; coveredSlots: number; mySlots: number };
type Slot = { startsAt: string; endsAt: string; past: boolean; people: { personId: string; name: string }[]; mine: boolean; full: boolean };
type Detail = { id: string; title: string; purpose: string | null; startsAt: string; endsAt: string; slotMinutes: number; maxPerSlot: number | null; canceled: boolean; canOrganize: boolean; slots: Slot[] };

const time = (iso: string) => new Date(iso).toLocaleTimeString('es', { hour: '2-digit', minute: '2-digit' });
const dayName = (iso: string) => new Date(iso).toLocaleDateString('es', { weekday: 'long', day: 'numeric', month: 'long' });

/** Tarjeta en Oración: cadenas activas y crear una. */
export function PrayerChainsCard() {
  const me = useMe();
  const base = `/prayer/churches/${me.activeChurchId}/chains`;
  const data = useAsync((signal) => (me.activeChurchId ? api.get<{ canOrganize: boolean; items: ChainItem[] }>(base, signal) : Promise.resolve(null)), [base]);
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  if (data.status !== 'ready' || !data.data) return null;
  const { canOrganize, items } = data.data;
  if (!canOrganize && items.length === 0) return null;
  return (
    <Card title="Cadenas de oración" action={canOrganize && !open ? <Button onClick={() => setOpen(true)}>Nueva cadena</Button> : null}>
      {open ? (
        <form
          className="form"
          onSubmit={(e) => {
            e.preventDefault();
            const f = new FormData(e.currentTarget);
            const start = new Date(String(f.get('start')));
            const hours = Number(f.get('hours') ?? 24);
            if (Number.isNaN(start.getTime())) return;
            setBusy(true);
            setError(null);
            api
              .post<{ id: string }>(base, {
                title: String(f.get('title') ?? '').trim(), purpose: String(f.get('purpose') ?? '').trim() || null, startsAt: start.toISOString(),
                endsAt: new Date(start.getTime() + hours * 3600_000).toISOString(), slotMinutes: Number(f.get('slot')), maxPerSlot: Number(f.get('max')) || null,
              })
              .then((r) => navigate(`/app/oracion/cadenas/${r.id}`))
              .catch((err) => { setError(errorMessage(err)); setBusy(false); });
          }}
        >
          {error ? <Alert>{error}</Alert> : null}
          <Field label="Nombre" name="title" required minLength={3} maxLength={120} defaultValue="24 horas de oración" />
          <div className="form__row form__row--3">
            <Field label="Empieza" name="start" type="datetime-local" required />
            <Field label="Horas" name="hours" type="number" min={1} max={192} defaultValue={24} />
            <div className="field">
              <label htmlFor="chain-slot">Bloques de</label>
              <select id="chain-slot" name="slot" defaultValue="30">
                <option value="15">15 minutos</option>
                <option value="20">20 minutos</option>
                <option value="30">30 minutos</option>
                <option value="60">1 hora</option>
              </select>
            </div>
          </div>
          <Field label="Personas por bloque (vacío = sin límite)" name="max" type="number" min={1} max={50} />
          <div className="field">
            <label htmlFor="chain-purpose">Motivo (opcional)</label>
            <textarea id="chain-purpose" name="purpose" rows={2} maxLength={2000} />
          </div>
          <div className="actions">
            <Button type="submit" busy={busy}>Crear y avisar a la iglesia</Button>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancelar</Button>
          </div>
        </form>
      ) : null}
      {items.length === 0 ? <p className="muted small">No hay cadenas activas.</p> : null}
      <ul className="list">
        {items.map((c) => (
          <li key={c.id} className="list__item">
            <Link className="list__link" to={`/app/oracion/cadenas/${c.id}`}>
              <strong>{c.title}</strong>
              <span className="small muted">{dayName(c.startsAt)} · {c.coveredSlots} de {c.totalSlots} bloques cubiertos{c.mySlots ? ` · tú tomaste ${c.mySlots}` : ''}</span>
            </Link>
          </li>
        ))}
      </ul>
    </Card>
  );
}

/** Una cadena: los bloques por día; tomar o soltar el propio. */
export function PrayerChainPage() {
  const { chainId = '' } = useParams();
  const me = useMe();
  const url = `/prayer/churches/${me.activeChurchId}/chains/${chainId}`;
  const data = useAsync((signal) => (me.activeChurchId ? api.get<Detail>(url, signal) : Promise.resolve(null)), [url]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  if (data.status === 'loading') return <div className="page"><Loading /></div>;
  if (data.status === 'error') return <div className="page"><ErrorState error={data.error} /></div>;
  const c = data.data;
  if (!c) return <EmptyState title="Elige primero una iglesia." />;
  const covered = new Set(c.slots.filter((s) => s.people.length).map((s) => s.startsAt)).size;
  const byDay = new Map<string, Slot[]>();
  for (const s of c.slots) {
    const k = dayName(s.startsAt);
    byDay.set(k, [...(byDay.get(k) ?? []), s]);
  }
  const toggle = (s: Slot) => {
    setBusy(s.startsAt);
    setError(null);
    api.put(`${url}/slots`, { startsAt: s.startsAt, take: !s.mine }).then(() => data.reload()).catch((e) => setError(errorMessage(e))).finally(() => setBusy(null));
  };

  return (
    <div className="page">
      <p className="small"><Link to="/app/oracion">← Oración</Link></p>
      <header className="page__header">
        <h1>{c.title}</h1>
        <p className="muted">{covered} de {c.slots.length} bloques cubiertos · toca un bloque para tomarlo</p>
        {c.purpose ? <p className="pre-line">{c.purpose}</p> : null}
      </header>
      {c.canceled ? <Alert>Esta cadena se canceló.</Alert> : null}
      {error ? <Alert>{error}</Alert> : null}
      <progress className="usage" max={c.slots.length} value={covered} aria-label="Bloques cubiertos" />
      {[...byDay].map(([day, slots]) => (
        <Card key={day} title={day.charAt(0).toUpperCase() + day.slice(1)}>
          <div className="chain-grid">
            {slots.map((s) => (
              <button
                key={s.startsAt}
                type="button"
                className={`chain-slot${s.mine ? ' chain-slot--mine' : s.people.length ? ' chain-slot--taken' : ''}`}
                disabled={c.canceled || s.past || busy === s.startsAt || (!s.mine && s.full)}
                aria-pressed={s.mine}
                onClick={() => toggle(s)}
                title={s.people.map((p) => p.name).join(', ')}
              >
                <strong>{time(s.startsAt)}</strong>
                <span className="small">{s.mine ? 'Tú' : s.people.length ? s.people.map((p) => p.name.split(' ')[0]).join(', ') : 'Libre'}</span>
              </button>
            ))}
          </div>
        </Card>
      ))}
      {c.canOrganize && !c.canceled ? (
        <div className="actions">
          <Button variant="ghost" onClick={() => { if (window.confirm('¿Cancelar la cadena?')) void api.post(`${url}/cancel`).then(() => data.reload()); }}>Cancelar cadena</Button>
        </div>
      ) : null}
    </div>
  );
}
